import { observable, action, computed } from 'mobx';
import sessionStore from '../session';
import performanceStore from '../performance';
import sessionIDListStore from '../sessionIDList';
import historyStore from '../history';
import firstInitStore from '../firstInit';
import agentStore from '../agent';
import { getTrans, handleHideBubble, handleRecoveryHistory } from '@/utils/tools';
import { formatFromInUrl, getQueryForm } from '@/utils/instantLoaded/toolsWithoutDeps';
import { handleResponse } from '@/utils';
import { getConfig, getSearchUrl, isBank, isSupportChatbot } from '@/config/core';
import { emergencyTemplatePromise } from '@/utils/somePromise/emergencyTemplatePromise';
import {
  SessionIDType,
  FAKE_SESSION_ID,
  CHATBOT_TYPE,
  SourceInUrl,
  TRANSIFY_KEY,
  APP_BOT_TYPE,
  PREMIUM_USER_ENTRY_POINT,
  LiveAgentControl,
  ALL_BOT_TYPE,
} from '@/utils/constants';
import { AGENT_AVATAR, BOT_AVATAR } from '@/utils/locale';
import { addEmergencyTemplateRemind } from '@/utils/agent';
import { debounceReportAgentMessage } from '@/utils/reportAgentMessage';
import getUnreadMessage from '@/utils/getUnreadMessage';
import { DIALOGUE_TYPE } from '@/types/common';
import { addPartnerChatWithAgent } from '@/utils/sessionUtils';
import { App } from '@/config/constants';
import { isLiveAgentScUIOptimisation } from '@/utils/experiment/getShortCutUIUXExperiment';

const formatShortcut = (intent) => {
  return {
    actual_message: intent.shortcut_text,
    query_callback: intent.query_callback,
    shortcut_id: intent.intent_id,
    shortcut_message: intent.shortcut_text,
    intent_type: intent.intent_type,
    intent_source: intent.intent_source,
    intent_id: intent.intent_id,
    is_live_agent: intent.is_live_agent,
    shortcut_type: intent.shortcut_type,
    need_hide_when_live_agent_shortcut_show: intent.need_hide_when_live_agent_shortcut_show,
    type: 1,
  };
};

// 初始化(欢迎语、HotQuestions、Shortcuts等)
export class InitStore {
  // @observable isFirstPaint = true;
  // 一次会话为一个session，包含多个问题
  @observable sessionId = '';

  @observable sessionIdKey = '';

  @observable sessionToken = '';

  @observable dateTime = new Date().getTime();

  // bot avatar
  @observable botAvatar = BOT_AVATAR;

  // bot nickname
  @observable botNickname;

  @observable agentAvatar = AGENT_AVATAR;

  @observable queueAvatar = BOT_AVATAR;

  @observable chattingAvatar = AGENT_AVATAR;

  @observable shortcutList = [];

  // 0: 旧的shortcut，1: 新的shortcut（intent prediction）
  @observable shortcutType = 0;

  // 用户命中的segments
  @observable hideLiveAgentSegmentIds = [];

  // 是否展示开启新地区聊天功能（后台接入AB Testing）
  @observable isDirectToLiveChatForNewMarket = true;

  @observable isFirstInit = true;

  // 是否展示 showChatbotCsat
  @observable showChatbotCsat = false;

  // 是否展示 含有ChatbotCsat的最新版本
  @observable csatRnBundleVersion = '';

  // 标识转人工的flag
  // 0: v1.0 salesforce SDK solution, 1: v2.0 CS chat flow by REST API solution
  @observable liveAgentChatVersion = 1;

  @observable appPolyfillVersion = 0;

  // 吸底bottom bar隐藏
  @observable shouldBottomBarHidden = false;

  // 灰度配置
  @observable grayFeatures;

  // 是否铂金用户
  // 1. 直接走 premium live chat
  // 2. 使用 premium chatbot
  @observable isPremiumUser;

  // 新消息文案
  @observable lastMessageContent = '';

  // 是否滚动超出一屏
  @observable isScrollMaxThanOneScreen = false;

  // 不要滚动到底部
  @observable dontScrollToBottom = false;

  // 正在加载国际化文案
  @observable loadingToGetI18N = true;

  // 是否显示过 点击历史消息按钮 提示
  @observable hasShowHistoryButtonToast = false;

  // 为什么要存一个window.location.search?
  // 因为从floating bubble 回到页面时，url上的参数会丢掉
  @observable searchUrl = getSearchUrl();

  @observable from = getQueryForm('from', this.searchUrl);
  @observable pageBeforeHc = getQueryForm('page_before_hc', this.searchUrl); // hc 页面记录了它上一个页面的 entry point 并透传过来
  // 用户从helpCentre进入chatbot时阅读的最后一篇文章的id
  @observable lastArticleID = getQueryForm('lastArticleID', this.searchUrl);

  // ab test 实验分组 signature
  @observable abTestSignature = '';

  // chatbot 类型
  @observable chatbotType = CHATBOT_TYPE.NORMAL_CHATBOT;

  // bot类型
  @observable appType = APP_BOT_TYPE.GENERAL;

  // 真的 bot 类型
  @observable botType = ALL_BOT_TYPE.UN_KNOWN_BOT;

  // main组件是否开始渲染
  @observable isMainRendered = false;

  // 是否有未读消息
  @observable hasUnreadAgentMessage = false;

  // 是否是PN回来的
  // 如果是会在消费后重置为false
  @observable isFromPN = false;

  // 当前页面是否是显示状态
  @observable isPageActive = true;

  @observable liveAgentShortcut;

  // liveAgent Shortcut应该插入的位置
  @observable liveAgentShortcutIndex;

  @observable shouldNotShowDynamicAnimation = false;

  @observable hideLiveAgentShortcuts = false;

  // pinned announcement 是否悬浮在顶部
  @observable pinnedAnnouncementShow = true;
  // pinned announcement dropdown open
  @observable pinnedAnnouncementDropdownOpen = false;
  // 设置原 announcement 的数据，如果原 announcement 不存在则不展示首屏 datetime，如果原 announcement 存在要一直展示首屏daytime
  @observable initAnnouncementsData = [];
  // pinned announcement 所需数据
  @observable pinnedAnnouncementData = [];

  // init session 传递的 chat config
  @observable initSessionChatConfig = { canUseInternetCall: false };

  // 查看系统是否为ios
  @observable isIOS = false;

  // 是否打开 live agent 功能
  liveAgentSetting;

  @observable inputBoxSuggestionIntent;

  @observable submitWebformSwitch = false;

  constructor() { }

  @computed
  get isPremium() {
    return this.chatbotType === CHATBOT_TYPE.PREMIUM_CHATBOT || this.chatbotType === CHATBOT_TYPE.PREMIUM_LIVE_CHAT;
  }

  @computed
  get isPremiumLiveChat() {
    return this.chatbotType === CHATBOT_TYPE.PREMIUM_LIVE_CHAT;
  }

  @action
  COLLECT_FIRSTINIT_STORE() {
    const { initStore, historyStore: firstHistoryStore } = firstInitStore;

    Object.keys(initStore).forEach((key) => {
      this[key] = initStore[key];
    });

    const { initMessageId, beforeMessageId } = firstHistoryStore || {};

    historyStore.setInitMessageId(initMessageId);
    historyStore.setBeforeMessageId(beforeMessageId);
  }

  @action
  INIT_CHATBOT() {
    const currentIsFirstInit = this.isFirstInit;

    this.UPDATE_IS_MAIN_RENDERED();

    currentIsFirstInit && this.COLLECT_FIRSTINIT_STORE();

    // 进入Chat页面，统一关闭浮窗
    handleHideBubble();

    // 用户是否是 premium user
    this.UPDATE_USER_TYPE();

    // 检查系统
    this.CHECK_DEVICE();

    // 首次访问 & 尝试恢复历史
    // 无需恢复则调用：FETCH_DATA
    currentIsFirstInit && handleRecoveryHistory();

    // @MARK: lazyLoad: { reportDeviceInfo }
    import('@/utils/bridges/getDeviceInfo').then(({ reportDeviceInfo }) => {
      reportDeviceInfo();
    });
  }

  @action
  UPDATE_IS_MAIN_RENDERED() {
    this.isMainRendered = true;
    firstInitStore.UPDATE_INITSTORE({ isMainRendered: true });
  }

  @action
  UPDATE_USER_TYPE() {
    const curEntryPoint = formatFromInUrl(this.from);
    const isPremiumUser = PREMIUM_USER_ENTRY_POINT.indexOf(curEntryPoint) !== -1;
    this.UPDATE_IS_PREMIUM_USER(isPremiumUser);

    if (isPremiumUser) {
      // 白金用户设置 默认值
      agentStore.UPDATE_AGENT_CASE_FIELD();
    }
  }

  @action
  CHECK_DEVICE() {
    const u = navigator.userAgent;
    const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1;
    const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
    this.isIOS = isIOS && !isAndroid;
  }

  @action
  async FETCH_DATA() {
    // 等待init_session结束
    const res = await firstInitStore.initPromise;
    const source = getQueryForm('__source__', this.searchUrl);
    const isFromPN = source === SourceInUrl.PUSH_NOTIFICATION;

    if (isFromPN) {
      const { unreadMessages } = await getUnreadMessage();
      const hasUnreadAgentMessage = unreadMessages?.length > 0;

      this.UPDATE_HAS_UNREAD_AGENT_MESSAGE(hasUnreadAgentMessage);

      this.INIT_DATETIME(this.dateTime);
      if (res) {
        handleResponse({ res }, !hasUnreadAgentMessage);
      }
    }

    // 直接连线 premium chat
    if (this.chatbotType === CHATBOT_TYPE.PREMIUM_LIVE_CHAT) {
      // init session 接口失败，伪造一个 fake sessionId
      this.UPDATE_SESSIONID(FAKE_SESSION_ID);
      this.UPDATE_IS_PREMIUM_USER(true);
      // agentStore.updateConnectPremiumLiveChatDirectly(true);
      if (!isFromPN) {
        // premiumUserConnect();
        this.addPremiumUserGreetingAndLiveChat();
      } else {
        this.addPremiumUserLivePrompt();
      }

      return;
    }
    // 无论什么app不支持chatbot的情况都需要推送进线消息
    if (!isSupportChatbot()) {
      if (res instanceof Error) {
        handleHideBubble({ status_num: 0, chat_status: 3 });
        await emergencyTemplatePromise;

        addEmergencyTemplateRemind();
        return;
      }

      this.initLiveChatSdk();
      return;
    }
  }

  @action
  SET_INIT_DATA(data) {
    const {
      session_id,
      session_token,
      live_agent_chat_version = 1,
      shortcuts,
      shortcut_intents,
      avatar,
      liveChatAvatar,
      dateTime,
      gray_features,
      app_polyfill_version,
      searchUrl,
      abTestSignature,
      shortcut_type = 1,
      chatbot_type = 1,
      hide_live_agent_segment_ids = [],
      is_direct_to_live_chat_for_new_market = true,
      app_type,
      isFromPN = false,
      chatbotType,
      csat_switch,
      csat_rn_version,
      live_agent_shortcut,
      live_agent_insert_idx,
      queueAvatar,
      chattingAvatar,
      hide_liveagent_shortcuts,
      bot_type,
      botNickname,
      hideLiveAgentShortcuts,
      liveAgentShortcut,
    } = data;

    session_id && (this.sessionId = session_id) && (this.sessionIdKey = session_id); // this.UPDATE_SESSIONID(session_id);
    session_token && this.UPDATE_SESSIONTOKEN(session_token);
    this.UPDATE_LIVE_AGENT_CHAT_VERSION(live_agent_chat_version);
    this.UPDATE_APP_POLYFILL_VERSION(app_polyfill_version);
    dateTime && this.UPDATE_DATETIME(dateTime);
    shortcuts && shortcut_type === 0 && this.UPDATE_SHORTCUT(shortcuts); // 旧的shortcut字段

    shortcut_intents && shortcut_type === 1 && this.UPDATE_SHORTCUT_INTENTS(shortcut_intents);
    this.UPDATE_SHORTCUT_TYPE(shortcut_type);
    avatar && this.UPDATE_BOT_AVATAR(avatar);
    hide_live_agent_segment_ids && this.UPDATE_HIDE_LIVE_AGENT_SEGMENT_IDS(hide_live_agent_segment_ids);
    csat_switch && this.UPDATE_SHOW_CHATBOT_CSAT(csat_switch);
    csat_rn_version && this.UPDATE_CSAT_RN_BUNDLE_VERSION(csat_rn_version);
    gray_features && this.UPDATE_GRAY_FEATURE(gray_features);
    searchUrl && this.UPDATE_SEARCH_URL(searchUrl);
    abTestSignature && this.UPDATE_AB_TEST_SIGNATURE(abTestSignature);
    chatbot_type && this.UPDATE_CHATBOT_TYPE(chatbot_type);
    chatbot_type === CHATBOT_TYPE.PREMIUM_CHATBOT && this.UPDATE_IS_PREMIUM_USER(true);
    this.UPDATE_APP_TYPE(app_type);
    this.UPDATE_BOT_TYPE(bot_type);
    this.agentAvatar = liveChatAvatar || this.agentAvatar;
    isFromPN && this.UPDATE_IS_FROM_PN(true);
    chatbotType && this.UPDATE_CHATBOT_TYPE(chatbotType);
    this.UPDATE_IS_DIRECT_TO_LIVE_CHAT_FOR_NEW_MARKET(is_direct_to_live_chat_for_new_market);
    live_agent_shortcut && this.UPDATE_LIVE_AGENT_SHORTCUT(live_agent_shortcut);
    this.UPDATE_LIVE_AGENT_SHORTCUT_INDEX(live_agent_insert_idx);
    this.queueAvatar = queueAvatar || this.queueAvatar;
    this.chattingAvatar = chattingAvatar || this.chattingAvatar;
    hide_liveagent_shortcuts
      && this.UPDATE_HIDE_LIVE_AGENT_SHORTCUTS(hide_liveagent_shortcuts);


    // 恢复会话来源的数据，直接使用
    this.botNickname = botNickname || this.botNickname;
    this.hideLiveAgentShortcuts = hideLiveAgentShortcuts || this.hideLiveAgentShortcuts;
    this.liveAgentShortcut = liveAgentShortcut || this.liveAgentShortcut;

    // 首次请求，添加greeting消息 & hotQuestions
    if (this.isFirstInit) {
      performanceStore.SET_FIRST_INTERACTIVE(window.performance.now());
      // init_session成功后，发性能埋点
      performanceStore.SEND_TRACK();

      // 把isFirstInit置为false
      this.UPDATE_NOTFIRST();
    }
  }

  @action
  UPDATE_NOTFIRST() {
    // 首次请求flag置为false
    this.isFirstInit = false;
  }

  @action
  UPDATE_SESSIONID(id = '') {
    this.sessionId = id;
    this.sessionIdKey = id;

    // 更新sessionId的同时，将其放入sessionIDList
    sessionIDListStore.UPDATE_SESSION_ID_LIST({
      type: SessionIDType.CHAT_BOT,
      id,
    });
  }

  @action
  ONLY_UPDATE_SESSION_ID(id = '') {
    this.sessionId = id;
    this.sessionIdKey = id;
  }

  @action
  UPDATE_SESSIONTOKEN(token = '') {
    this.sessionToken = token;
  }

  @action
  UPDATE_APP_TYPE(appType) {
    this.appType = appType;
  }

  @action
  UPDATE_BOT_TYPE(botType) {
    this.botType = botType;
  }

  @action
  UPDATE_DATETIME(time) {
    this.dateTime = time;
  }

  @action
  INIT_DATETIME(dateTime) {
    const dialogueList = {
      from: 'system',
      type: 'DateTime',
      data: { time: dateTime },
    };
    sessionStore.ADD_INIT_LIST(dialogueList);
  }

  @action
  UPDATE_SHORTCUT(shortcutList) {
    this.shortcutList = shortcutList;
  }

  @action
  UPDATE_BOT_AVATAR(avatar) {
    this.botAvatar = avatar;
  }

  @action
  UPDATE_HIDE_LIVE_AGENT_SEGMENT_IDS(hide_live_agent_segment_ids) {
    this.hideLiveAgentSegmentIds = hide_live_agent_segment_ids;
  }

  @action
  UPDATE_LIVE_AGENT_CHAT_VERSION(liveAgentChatVersion) {
    this.liveAgentChatVersion = liveAgentChatVersion;
  }

  @action
  UPDATE_APP_POLYFILL_VERSION(appPolyfillVersion) {
    this.appPolyfillVersion = appPolyfillVersion;
  }

  @action
  UPDATE_SHOW_CHATBOT_CSAT(csat_switch) {
    this.showChatbotCsat = csat_switch;
  }

  @action
  UPDATE_CSAT_RN_BUNDLE_VERSION(csat_rn_version) {
    this.csatRnBundleVersion = csat_rn_version;
  }

  @action
  UPDATE_IS_DIRECT_TO_LIVE_CHAT_FOR_NEW_MARKET(isDirectToLiveChatForNewMarket) {
    this.isDirectToLiveChatForNewMarket = isDirectToLiveChatForNewMarket;
  }

  @action
  UPDATE_BOTTOMBAR_HIDDEN(flag = false) {
    this.shouldBottomBarHidden = flag;
  }

  @action
  UPDATE_GRAY_FEATURE(features = []) {
    this.grayFeatures = features;
  }

  @action
  UPDATE_IS_PREMIUM_USER(isPremiumUser) {
    this.isPremiumUser = isPremiumUser;
  }

  @action
  UPDATE_SCREEN_MAX_THEN_ONE_SCREEN(isScrollMaxThanOneScreen) {
    this.isScrollMaxThanOneScreen = isScrollMaxThanOneScreen;
  }

  @action
  UPDATE_LAST_MESSAGE_CONTENT(lastMessageContent) {
    this.lastMessageContent = lastMessageContent;
  }

  @action
  UPDATE_DONT_SCROLL_TO_BOTTOM(dontScrollToBottom) {
    this.dontScrollToBottom = dontScrollToBottom;
  }

  @action
  UPDATE_LOADING_TO_GET_I18N(loadingToGetI18N) {
    this.loadingToGetI18N = loadingToGetI18N;
  }

  @action
  UPDATE_HAS_SHOW_HISTORY_BUTTON_TOAST(hasShowHistoryButtonToast) {
    this.hasShowHistoryButtonToast = hasShowHistoryButtonToast;
  }

  @action
  UPDATE_SEARCH_URL(searchUrl) {
    this.searchUrl = searchUrl;
  }

  @action
  UPDATE_AB_TEST_SIGNATURE(abTestSignature) {
    this.abTestSignature = abTestSignature;
  }
  UPDATE_SHORTCUT_INTENTS(shortcut_intents) {
    const formatToShortcut = shortcut_intents.map(formatShortcut);

    this.UPDATE_SHORTCUT(formatToShortcut);
  }

  @action
  UPDATE_SHORTCUT_TYPE(shortcutType) {
    this.shortcutType = shortcutType;
  }

  @action
  UPDATE_CHATBOT_TYPE(chatbotType) {
    this.chatbotType = chatbotType;
  }

  @action
  initLiveChatSdk() {
    if (getConfig()?.app === App.Partner) {
      addPartnerChatWithAgent(this.from);
      return;
    }
    console.log('添加live chat气泡');
    const { greeting, liveChatKey } = getConfig();
    const dialogueList = [
      {
        from: 'bot',
        type: DIALOGUE_TYPE.Msg,
        data: { content: getTrans(greeting) },
      },
      {
        from: 'bot',
        type: DIALOGUE_TYPE.Live,
        data: {
          msgContent: getTrans(liveChatKey) || getTrans('msg_live'),
          btnContent: getTrans('btn_live'),
          showPolicy: isBank(),
        },
      },
    ];
    if (isBank() || getTrans(greeting)?.length === 0) {
      dialogueList.shift();
    }
    sessionStore.ADD_INIT_LIST(dialogueList);
  }

  @action
  UPDATE_HAS_UNREAD_AGENT_MESSAGE(hasUnreadAgentMessage) {
    this.hasUnreadAgentMessage = hasUnreadAgentMessage;
  }

  @action
  UPDATE_IS_FROM_PN(isFromPN) {
    this.isFromPN = isFromPN;
  }

  @action
  UPDATE_IS_PAGE_ACTIVE(isPageActive) {
    this.isPageActive = isPageActive;

    if (isPageActive) {
      debounceReportAgentMessage();
    }
  }

  @action
  addPremiumUserLivePrompt() {
    const dialogueList = {
      from: 'bot',
      type: DIALOGUE_TYPE.Live,
      data: {
        msgContent: getTrans(this.isFromPN ? TRANSIFY_KEY.MSG_TO_LIVE : TRANSIFY_KEY.PREMIUM_MSG_TO_LIVE),
        btnContent: getTrans(TRANSIFY_KEY.BTN_LIVE),
      },
    };
    sessionStore.ADD_INIT_LIST(dialogueList);
  }

  @action
  addPremiumUserGreetingAndLiveChat() {
    const dialogueList = {
      from: 'bot',
      type: DIALOGUE_TYPE.Msg,
      data: { content: getTrans(TRANSIFY_KEY.PREMIUM_GREETING_TO_LIVE) },
    };
    sessionStore.ADD_INIT_LIST(dialogueList);
    this.addPremiumUserLivePrompt();
  }

  @action
  UPDATE_cancelFromAgent() {
    // 为了保证同样sessionId下，key值唯一，每次cancel在当前key值后缀'_c'
    this.sessionIdKey = this.sessionIdKey + '_c';

    sessionIDListStore.UPDATE_SESSION_ID_LIST({
      type: SessionIDType.CHAT_BOT,
      id: this.sessionIdKey,
    });
  }

  @action
  UPDATE_INTENT_PREDICTION_SHORTCUT_IF_ALLOWED(intentShortcuts) {
    this.UPDATE_SHORTCUT_INTENTS(intentShortcuts);
  }

  @action
  UPDATE_LIVE_AGENT_SHORTCUT(shortcut) {
    this.liveAgentShortcut = formatShortcut(shortcut);
  }

  @action
  UPDATE_LIVE_AGENT_SHORTCUT_INDEX(index) {
    this.liveAgentShortcutIndex = isLiveAgentScUIOptimisation() ? 0 : index;
  }

  @action
  UPDATE_SHOULD_NOT_SHOW_DYNAMIC_ANIMATION(boolean) {
    this.shouldNotShowDynamicAnimation = boolean;
  }

  @action
  UPDATE_HIDE_LIVE_AGENT_SHORTCUTS(control) {
    switch (control) {
      case LiveAgentControl.hide:
        this.hideLiveAgentShortcuts = true;
        break;
      case LiveAgentControl.show:
        this.hideLiveAgentShortcuts = false;
        break;
      case LiveAgentControl.remain:
      default:
        break;
    }
  }

  @action
  UPDATE_INPUT_BOX_SUGGESTION_INTENT(intent) {
    this.inputBoxSuggestionIntent = intent;
  }

  @action
  UPDATE_PINNED_ANNOUNCEMENT_DROPDOWN_OPEN(bool) {
    this.pinnedAnnouncementDropdownOpen = bool;
  }

  @action
  UPDATE_PINNED_ANNOUNCEMENT_SHOW(bool) {
    this.pinnedAnnouncementShow = bool;
  }

  @action
  UPDATE_PINNED_ANNOUNCEMENT_DATA(announcements) {
    this.pinnedAnnouncementData = announcements ?? [];
  }

  @action
  UPDATE_INIT_ANNOUNCEMENT_DATA(announcements) {
    this.initAnnouncementsData = announcements ?? [];
  }

  @action
  UPDATE_INIT_SESSION_CHAT_CONFIG(config) {
    this.initSessionChatConfig = config ?? {};
  }
}

const initStore = new InitStore();

export default initStore;
